/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

import { useState, useEffect } from 'react'
import { Brain, Key, FolderOpen, Sparkles, Users, MessageSquare, CheckCircle2, ArrowRight, GitBranch, AlertCircle } from 'lucide-react'
import { listProjects, type Project } from '@/lib/storage'

interface OnboardingModalProps {
  onComplete: () => void
  onCreateProject: () => void
  onOpenRepo: () => void
  onOpenGitHubRepo: () => void
  onOpenSettings: () => void
  onSelectProject: (projectId: string) => void
  hasApiKeys: boolean
  hasGitHubToken?: boolean
}

export function OnboardingModal({ onComplete, onCreateProject, onOpenRepo, onOpenGitHubRepo, onOpenSettings, onSelectProject, hasApiKeys, hasGitHubToken }: OnboardingModalProps) {
  const [existingProjects, setExistingProjects] = useState<Project[]>([])

  useEffect(() => {
    // Load existing projects
    const loadExistingProjects = async () => {
      const projects = await listProjects()
      setExistingProjects(projects)
    }
    loadExistingProjects()
  }, [])
  const [step, setStep] = useState<'welcome' | 'setup'>('welcome')

  const handleNext = () => {
    if (step === 'welcome') {
      setStep('setup')
    }
  }

  const handleCreateProject = () => {
    onComplete()
    onCreateProject()
  }

  const handleOpenRepo = () => {
    onComplete()
    onOpenRepo()
  }

  const handleOpenGitHubRepo = () => {
    onComplete()
    onOpenGitHubRepo()
  }

  const handleSetupApiKeys = () => {
    onOpenSettings()
  }

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-background-secondary border border-border rounded-lg w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        
        {/* Welcome Step */}
        {step === 'welcome' && (
          <>
            {/* Header */}
            <div className="p-8 border-b border-border text-center">
              <div className="flex items-center justify-center gap-3 mb-4">
                <svg className="w-12 h-12 fill-accent" viewBox="0 0 864 864" xmlns="http://www.w3.org/2000/svg">
                  <path d="M450.82,770.03c-20.46-20.36-21.71-37.88-20.69-65.47l-21.16-.08-.33,24.56c-.1,7.36-4.16,18.48-7.55,24.93-13.18,25.09-43.72,40.22-71.26,41.85-46.02,3.89-95.4-24.22-111.8-67.67-39.3,9.19-86.26-15.62-105.22-50.35-11.93-18.78-17.7-48.39-14.24-71-29.23-17.17-49.67-46.8-53.92-80.54-3.55-28.17,2.28-54.76,19.63-77.48-32.7-39.79-22.33-103.54,17.52-134.53-11.65-36.62-3.92-71.42,21.24-99.03,9.04-9.92,19.49-16.88,31.89-21.95.04-8.26-.88-15.97.53-23.85,6-33.52,30.2-60.49,62.14-71.69,14.68-5.15,29.12-7.47,44.94-5.01,26.16-43.99,87.63-55.19,130.74-30.47,19.42,11.13,35.01,29.82,35.16,52.82l.38,58.87,21.27-.04.58-58.74c.22-22.24,16.05-40.79,34.56-52.26,17.61-10.92,38.06-14.69,58.56-13.24,28.77,1.7,57.19,17.19,72.66,42.96,25.74-3.14,51.69,3.68,72.8,19.74,24.89,18.94,38.02,49.47,35.39,80.85,13.28,6.6,24.83,13.88,34.28,24.87,22.73,26.42,30.39,62.69,18.59,96.09,40.37,31.54,49.83,93.94,17.71,134.64,38.85,50.77,20.36,126.36-33.69,157.52l.09,14.01c.34,54.55-38.59,101.57-92.66,109.23-9.42,1.33-18.49-.13-27.84-1.41-19.73,50.16-77.48,77.48-129.18,64.75-15.83-3.9-29.69-11.52-41.13-22.89ZM243.29,663c-2.33,24,.57,48.25,15.89,67.52,23.52,29.58,70.37,43.25,100.9,17.62,17.1-14.36,14.42-29.47,14-50.65-.17-8.41-3.89-15.64-10.04-21.3l-59.6-54.75c-5.48-5.04-10.65-11.77-10.91-19.34l-.87-25.24c-.19-5.61-5.57-10.63-9.26-14.62l-20.48-22.1c-6.86-7.4-9.78-16.3-9.77-26.32l.06-59.73c.01-11.68-7.64-21.7-18.17-26.19-17.68-7.54-34.62-14.52-51.93-23.14-2.8-1.39-5.1-6.49-5.24-9.54-.28-6.46,6.44-11.67,12.62-9.93l34.67,9.77c9.92,2.8,19.06-.38,24.75-8.75l27.12-39.88c2.54-3.74,4.97-8.51,4.98-13.14l.07-29.48c.02-10.56-5.64-18.72-13.43-25.23-11.91-9.96-23.29-19.69-34.31-30.57-3.52-3.48-5.22-9.19-3.76-13.75,1.09-3.42,4.94-5.64,7.59-6.31,4.14-1.04,6.86.94,10.19,3.06l32.92,20.95c3.92,2.5,10.45,6.99,15.24,3.33,10.31-7.89,19.58-16.73,29.35-25.25,5-4.35,9.66-8.64,14.3-13.28,4.05-18.9,16.15-31.05,34.21-38.02l-.16-56.67c-.03-11.54-7.12-21.49-16.47-28.17-20.79-14.83-49.53-13.7-70.35,1.16-14.04,10.01-21.17,26.25-19.65,43.4,1.98,22.42,13.65,36.02,3.33,41.47-3.67,1.94-8.33,1.33-11.28-2.08-11.03-12.75-17.49-28.3-20.77-44.84-.96-4.86-3.6-7.25-8.85-7.13-28.98.66-54.39,20.95-60.45,49.42-1.81,8.5-1.37,16.95,2.17,24.75,3.31,7.32,1.29,15.31-6.83,18.43l-9.35,3.59c-33.21,12.75-52.45,43.24-41.31,78.84,27.34-6.86,55.24-3.75,80.29,6.04,4.5,1.76,6.69,7.11,5.87,11.29-.66,3.36-4.17,7.56-8.49,8.32-12.63,2.23-34.08-2.91-60.08,2.72-21.98,4.76-40.24,18.93-48.14,40.09-5.75,15.39-5.49,32.5.8,47.2,5.98,13.96,15.95,23.21,29.71,28.82l69.5,28.35c5.39,2.2,8.57,7.22,8.2,12.19-.34,4.61-3.76,9.09-9.21,10.12-4.4.83-9.85.63-14.58-.43-26.18-5.86-52.39-12.99-76.91-24.57-9.8,12.22-13.89,27.16-13.29,42.95,1.15,30.2,19.71,56.1,47.5,68.03,28.13,12.08,61.9,7.22,90.56-.91,5.52-1.57,10.93,1.07,12.69,5.83,1.55,4.21.45,9.91-4.02,13.03-26.3,18.3-59.89,23.06-91.68,19.75-1.84,35.09,21.37,66.51,55.98,74.34,8.03,1.82,15.68,1.99,23.55.15-.12-10.76,10.33-45.63,24.26-41.61,4.45,1.28,6.86,5.7,6.41,10.37ZM604.87,652.28c12.05,1.13,21.37,31.06,22.27,42.58,15.75,2.56,30.47-1.28,43.81-9.17,23.59-13.95,36.54-38.95,35.28-66.07-31.91,4.65-66.71-2.8-92.46-20.43-4.74-3.25-4.54-9.97-2.73-13.37,2.49-4.66,7.39-6.3,12.75-4.79,20,5.62,39.84,9.07,61.01,7.87,36.29-2.06,66.35-26.21,74.81-61.86,4.74-19.98.32-39.83-11.55-55.97-19.6,9.17-39.05,15.13-59.46,20.35-9.46,2.42-18.51,4.37-28.16,5.51-5.51-.33-10.09-2.57-12.19-7.08s-1.04-10.05,3.4-13.3c3.23-2.37,7.19-4.49,11.09-6.06l59.85-24.08c11.61-4.67,20.61-10.99,27.49-21.52,19.72-30.16,9.16-70.47-22.56-87.79-11.39-6.22-23.47-9.24-36.79-10.18-20.49-1.44-24.95.96-42.21.04-5.23-.28-8.98-4.4-9.53-8.47-.61-4.6,1.57-9.57,6.48-11.52,44.85-17.8,76.68-5.22,79.66-7.55.84-.66,1.71-2.46,2.02-3.95,3.83-18.23-.6-36.62-12.08-51.4-10.09-12.99-24.72-20.14-39.88-25-9.43-3.03-11.29-12.68-7.69-20.42,4.38-9.42,3.46-19.61.45-29.49-7.79-25.62-32.48-43.18-59.17-43.2-4.55,0-6.84,1.94-7.76,6.01-3.76,16.7-11.03,31.93-21.56,45.26-3.01,3.81-7.15,4.68-11.15,2.88-10.6-4.76,2.13-22.58,3.33-44.81.6-11.11-2.88-22.53-10.02-30.85-20.28-23.64-54.14-27.78-79.76-10.67-8.26,6.75-16.7,15.37-16.76,26.65l-.28,57.97c15.04,6.15,27.79,16.4,32.35,31.68,1.28,4.29,2.02,8.03,5.58,11.02l38.64,33.36c4.96,4.28,11.9.78,15.9-2.77,22.61-20.11,33.77-23.64,38.93-54.63.52-3.14,3.27-6.4,5.34-7.7,2.74-1.71,7.42-1.83,10.01-.26,4.42,2.69,5.11,7.53,5.3,12.26.58,14.34-1.95,28.05-9.77,40.03-5.99,9.17-12.9,17.31-21.08,24.6l-17.76,15.83c-6.19,5.52-11.14,13.36-11.21,21.99l-.2,27.56c-.03,4.44,1.3,9.74,3.86,13.44l27.79,40.08c6.01,8.67,14.45,13.45,25.47,10.34l35.39-9.98c5.27-1.49,10.56,2.65,12.13,7.19,1.44,4.16-1.1,9.96-5.88,12.5-15.79,8.4-31.91,14.78-48.6,21.87-10.47,4.45-20.63,12.97-20.74,25.2l-.61,68.21c-.06,7.11-4.51,14.45-9.07,19.54l-24.38,27.24c-15.01,16.77,3.74,35.35-16.88,54.22l-60.86,55.7c-5.09,4.66-9.19,11.71-9.53,18.54-.45,8.97-.53,17.49-.02,26.37.84,14.59,10.79,26.37,23.37,33.04,12.53,6.65,26.27,7.75,40.33,5.67,26.55-3.92,48.64-20.45,59.85-44.8,17.22-37.42-1.48-65.08,16.36-63.42ZM516.49,285.73c2.3,0,4.16-.5,4.06-1.33-.07-.63-.7-2.47-1.72-3.35l-49.14-42.44-99.13-.39c-2.46,1.78-4.92,3.96-7.08,6.14l-42.93,37.19c-.9.78-1.29,4.07.14,4.07l195.8.11ZM557.99,402.24l-31.62-46.67-213.5.09-28.61,41.95c-.71,1.03-1.92,4.76-.25,4.76l273.97-.13ZM550.89,514.82l.13-42.47-262.28.05.36,46.63,256.49.03c3.04,0,5.29-1.33,5.3-4.24ZM466.1,636.42l43.99-39.54c1.05-2.27,1.13-4.67.35-7.82l-180.6-.04c-1.32,2.25-1.4,5.04-.71,7.55l43.72,39.74,93.25.11Z"/>
                </svg>
                <h1 className="text-4xl font-title">Welcome to HiveMind</h1>
              </div>
              <p className="text-lg text-foreground-muted">
                Multi-LLM orchestration for collaborative creative work
              </p>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-8">
              <div className="space-y-6 max-w-2xl mx-auto">
                
                {/* What is HiveMind */}
                <div className="space-y-3">
                  <h2 className="text-2xl font-title flex items-center gap-2">
                    <Sparkles className="w-6 h-6 text-accent" />
                    What is HiveMind?
                  </h2>
                  <p className="text-foreground-muted leading-relaxed">
                    HiveMind coordinates multiple AI personas to provide comprehensive feedback on your creative work. 
                    Instead of getting feedback from just one AI model, you get diverse perspectives from a panel of 
                    specialized reviewers.
                  </p>
                </div>

                {/* How it works */}
                <div className="space-y-4">
                  <h3 className="text-xl font-title">How it works:</h3>

                  <div className="grid gap-4">
                    {/* Step 1 */}
                    <div className="flex gap-4 p-4 bg-background-tertiary rounded-lg border border-border">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-accent/20 text-accent flex items-center justify-center font-bold">
                          1
                        </div>
                      </div>
                      <div>
                        <h4 className="font-bold mb-1 flex items-center gap-2">
                          <MessageSquare className="w-4 h-4" />
                          Brainstorm Mode
                        </h4>
                        <p className="text-sm text-foreground-muted">
                          Start in Brainstorm mode to explore ideas naturally with the orchestrator. Have a conversation,
                          ask questions, and refine concepts until you're ready for structured review.
                        </p>
                      </div>
                    </div>

                    {/* Step 2 */}
                    <div className="flex gap-4 p-4 bg-background-tertiary rounded-lg border border-border">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-accent/20 text-accent flex items-center justify-center font-bold">
                          2
                        </div>
                      </div>
                      <div>
                        <h4 className="font-bold mb-1 flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          Peer Review Mode
                        </h4>
                        <p className="text-sm text-foreground-muted">
                          Switch to Peer Review mode when ready. The orchestrator compiles your brainstorm into a project document,
                          then sends it to multiple AI personas for structured feedback using MOTH format.
                        </p>
                      </div>
                    </div>

                    {/* Step 3 */}
                    <div className="flex gap-4 p-4 bg-background-tertiary rounded-lg border border-border">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-accent/20 text-accent flex items-center justify-center font-bold">
                          3
                        </div>
                      </div>
                      <div>
                        <h4 className="font-bold mb-1 flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4" />
                          Judge Evaluation & Iteration
                        </h4>
                        <p className="text-sm text-foreground-muted">
                          A judge AI evaluates all peer feedback and provides a quality score. If approved, you're done!
                          If not, request revisions from the orchestrator or continue brainstorming.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Key Features */}
                <div className="p-4 bg-accent/10 border border-accent/30 rounded-lg">
                  <h3 className="font-bold mb-2 text-accent">Key Features:</h3>
                  <ul className="space-y-1 text-sm text-foreground-muted">
                    <li>• <strong>Dual Mode Workflow</strong> - Brainstorm naturally, then switch to structured peer review</li>
                    <li>• <strong>Drag-and-Drop Files</strong> - Drag files and images from desktop for AI analysis</li>
                    <li>• <strong>111 Specialized Personas</strong> - From code reviewers to creative writers</li>
                    <li>• <strong>PDF Export & MOTHify</strong> - Export to PDF, compress documents by 70-90%</li>
                    <li>• <strong>Multiple LLM Support</strong> - OpenAI, Anthropic, Google, xAI, OpenRouter</li>
                    <li>• <strong>Browser-Based</strong> - All data stored locally in your browser</li>
                    <li>• <strong>Smart Features</strong> - Auto review, preserve context, LLM logs, clear chat</li>
                    <li>• <strong>MOTH Format</strong> - Compact specification format optimized for LLMs</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-border flex justify-end">
              <button
                onClick={handleNext}
                className="btn-primary px-6 py-3 flex items-center gap-2"
              >
                Get Started
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </>
        )}

        {/* Setup Step */}
        {step === 'setup' && (
          <>
            {/* Header */}
            <div className="p-6 border-b border-border">
              <h2 className="text-2xl font-title mb-2">Let's Get You Set Up</h2>
              <p className="text-sm text-foreground-muted">
                Two quick steps to start using HiveMind
              </p>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-6 max-w-2xl mx-auto">
                
                {/* Step 1: API Keys */}
                <div className="p-6 bg-background-tertiary rounded-lg border-2 border-border">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${hasApiKeys ? 'bg-green-500/20 text-green-400' : 'bg-accent/20 text-accent'}`}>
                        {hasApiKeys ? <CheckCircle2 className="w-6 h-6" /> : <Key className="w-6 h-6" />}
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-title mb-2">
                        Step 1: Configure API Keys & Models
                      </h3>
                      <p className="text-sm text-foreground-muted mb-4">
                        HiveMind needs API keys to communicate with LLM providers. The Settings modal has comprehensive
                        information to help you choose the best models and APIs for your needs.
                      </p>

                      {hasApiKeys ? (
                        <div className="space-y-3">
                          <div className="flex items-center gap-2 text-sm text-green-400">
                            <CheckCircle2 className="w-4 h-4" />
                            <span>API keys configured!</span>
                          </div>
                          <div className="text-xs text-foreground-muted bg-background/50 rounded p-3 border border-border">
                            <p className="font-semibold mb-2">💡 Explore Settings for:</p>
                            <ul className="space-y-1 ml-4">
                              <li>• <strong>Model Selection</strong> - Organized by cost/quality tiers with pricing</li>
                              <li>• <strong>LLM Leaderboards</strong> - Rankings from LMArena, Aider, HuggingFace</li>
                              <li>• <strong>Cost Savings</strong> - Ultra-low cost providers (5-225x cheaper!)</li>
                              <li>• <strong>Model Tiers</strong> - Quick selection by budget and geographic origin</li>
                            </ul>
                          </div>
                        </div>
                      ) : (
                        <>
                          <button
                            onClick={handleSetupApiKeys}
                            className="btn-primary px-4 py-2 flex items-center gap-2"
                          >
                            <Key className="w-4 h-4" />
                            Open Settings
                          </button>
                          <div className="text-xs text-foreground-muted mt-3 space-y-2">
                            <p>
                              <strong>Recommended:</strong> Get an OpenRouter API key at{' '}
                              <a href="https://openrouter.ai/keys" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
                                openrouter.ai/keys
                              </a>
                              {' '}(supports all models with one key)
                            </p>
                            <p className="bg-background/50 rounded p-2 border border-border">
                              <strong>💰 Save Money:</strong> Settings has info on ultra-low cost providers like DeepSeek ($0.014/M),
                              Groq ($0.20/M), and Qwen 3 ($0.08/M) - up to 225x cheaper than OpenRouter!
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Step 2: Create Project */}
                <div className={`p-6 bg-background-tertiary rounded-lg border-2 ${hasApiKeys ? 'border-accent' : 'border-border opacity-50'}`}>
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 rounded-full bg-accent/20 text-accent flex items-center justify-center">
                        <FolderOpen className="w-6 h-6" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-title mb-2">
                        Step 2: Create or Open a Project
                      </h3>
                      <p className="text-sm text-foreground-muted mb-4">
                        Projects organize your work and conversations. You can create a new project, open a local
                        folder, clone a GitHub repository, or continue with a previous project.
                      </p>

                      {/* Existing Projects */}
                      {existingProjects.length > 0 && (
                        <div className="mb-4">
                          <p className="text-xs font-condensed text-foreground-secondary mb-2">
                            Continue with existing project:
                          </p>
                          <div className="space-y-2 max-h-32 overflow-y-auto">
                            {existingProjects.map((project) => (
                              <button
                                key={project.id}
                                onClick={() => {
                                  onComplete()
                                  onSelectProject(project.id)
                                }}
                                disabled={!hasApiKeys}
                                className="w-full text-left px-3 py-2 bg-background-secondary border border-border rounded hover:border-accent hover:bg-background-tertiary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <div className="flex items-center gap-2">
                                  <FolderOpen className="w-4 h-4 text-accent" />
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm font-condensed truncate">{project.name}</div>
                                    {project.description && (
                                      <div className="text-xs text-foreground-muted truncate">{project.description}</div>
                                    )}
                                  </div>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="flex flex-col gap-3">
                        <button
                          onClick={handleCreateProject}
                          disabled={!hasApiKeys}
                          className="btn-primary px-4 py-2 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Brain className="w-4 h-4" />
                          Create New Project
                        </button>

                        <button
                          onClick={handleOpenRepo}
                          disabled={!hasApiKeys}
                          className="btn-ghost px-4 py-2 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <FolderOpen className="w-4 h-4" />
                          Open Local Folder
                        </button>

                        <button
                          onClick={handleOpenGitHubRepo}
                          disabled={!hasApiKeys}
                          className="btn-ghost px-4 py-2 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <GitBranch className="w-4 h-4" />
                          Open GitHub Repository
                        </button>
                      </div>

                      {!hasApiKeys && (
                        <p className="text-xs text-yellow-400 mt-3">
                          ⚠️ Please configure API keys first
                        </p>
                      )}

                      {hasApiKeys && !hasGitHubToken && (
                        <div className="text-xs text-foreground-muted mt-3 bg-background/50 rounded p-3 border border-border">
                          <div className="flex items-start gap-2">
                            <AlertCircle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="font-semibold mb-1">GitHub Personal Access Token</p>
                              <p>
                                To clone private repositories, add your GitHub PAT in Settings.
                                Public repositories can be cloned without a token using the URL option.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-border">
              <p className="text-xs text-foreground-muted text-center">
                Your API keys and project data are stored only in your browser. 
                HiveMind has no backend server and never sends your data anywhere except directly to LLM providers.
              </p>
            </div>
          </>
        )}

      </div>
    </div>
  )
}

